import SeoHead from "../../components/Seo/SeoHead";
import WhatsAppMockup from "../../components/Marketing/WhatsAppMockup";
import FaqAccordion from "../../components/Marketing/FaqAccordion";
import { Link } from "react-router-dom";
import {
  TbDroplet,
  TbCalendar,
  TbClock,
  TbCoin,
  TbBell,
  TbSparkles,
  TbCircleCheck,
} from "react-icons/tb";
import styles from "./LavaJatos.module.css";
import {
  BUSINESS_SEGMENTS,
  CTA_PAGES,
  CTA_PLACEMENTS,
  DESTINATIONS,
  trackTrialCtaClick,
} from "../../analytics";
import {
  TRIAL_PRODUCTS,
  buildTrialSignupPath,
} from "../../constants/trialProduct";

const LAVAJATOS_FAQ = [
  {
    question: "E se o cliente quiser cancelar ou remarcar?",
    answer:
      "O cliente cancela ou remarca direto pelo WhatsApp, em segundos. O horário liberado entra para a fila de espera automaticamente — sem você precisar intervir.",
  },
  {
    question: "O bot consegue fazer upsell automático?",
    answer:
      "Sim. No momento do agendamento, o bot oferece serviços adicionais como enceramento, higienização de estofamento ou polimento — aumentando o ticket sem esforço.",
  },
  {
    question: "Funciona para carros e motos?",
    answer:
      "Sim. Você cadastra cada serviço com seu preço e sua duração — lavagem simples de moto e lavagem completa de carro são serviços diferentes —, e o bot usa a duração para ocupar o tempo certo na agenda.",
  },
  {
    question: "Posso oferecer pacote de assinatura mensal?",
    answer:
      "O pacote você pode cadastrar como serviço, e o bot agenda cada lavagem normalmente. O que ele ainda não faz é controlar saldo: quantas lavagens o assinante já usou no mês é conta sua, não do bot.",
  },
  {
    question: "O cliente recebe aviso quando o carro estiver pronto?",
    answer:
      "Sim. Quando o serviço for concluído, o bot manda mensagem automática avisando que o carro está pronto para retirada.",
  },
  {
    question: "Posso controlar quantos carros entram por horário?",
    answer:
      "Sim. Você define a capacidade por slot de tempo e o bot só permite agendamentos dentro dessa capacidade — evitando a fila surpresa.",
  },
];

const LAVAJATOS_MOCKUP = [
  { type: "out", text: "Boa tarde, tem fila aí?" },
  {
    type: "in",
    text: "Boa tarde! Hoje temos 5 carros na fila · ~45 min de espera.",
  },
  {
    type: "in",
    text: "Quer reservar um horário pra não esperar?",
    buttons: ["Reservar 14h", "Reservar 15h", "Vou agora mesmo"],
  },
  { type: "out", text: "Reservar 14h" },
  {
    type: "in",
    text: "✅ Reservado! Te esperamos às 14h. Lavagem completa, R$ 60.",
  },
];

const SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Chatbot de WhatsApp para Lava-Jato",
  provider: { "@type": "Organization", name: "Nexus Chatbot" },
  areaServed: "BR",
  serviceType: "Chatbot de WhatsApp para Lava-Jato",
};

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: LAVAJATOS_FAQ.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const DORES = [
  {
    Icon: TbCalendar,
    title: "Pico sem controle",
    text: "Sexta e sábado viram caos. Agendamento distribui a demanda e acaba com a fila surpresa.",
  },
  {
    Icon: TbClock,
    title: "Espera imprevisível",
    text: "Cliente chega e encontra 5 carros na frente. Com agendamento, sabe exatamente quando chegar.",
  },
  {
    Icon: TbCoin,
    title: "Upsell esquecido",
    text: "Atendente não oferece cera, polimento ou interior. O bot oferece automaticamente no momento do agendamento.",
  },
  {
    Icon: TbBell,
    title: "Cliente sumiu?",
    text: "Faz 3 meses sem lavar. O bot manda lembrete de higienização e traz o cliente de volta.",
  },
];

const RECURSOS = [
  "Agendamento com controle de capacidade por horário",
  "Seleção de pacote (simples, completa, premium, detailing)",
  "Upsell automático (enceramento, higienização, polimento)",
  "Estimativa de tempo de serviço informada ao cliente",
  "Notificação automática quando o carro estiver pronto",
  "Assinatura mensal de lavagens recorrentes",
];

/** Contexto fixo desta página para o funil de teste grátis (GA4). */
const CTA_CTX = {
  cta_page: CTA_PAGES.LAVA_JATOS,
  business_segment: BUSINESS_SEGMENTS.LAVA_JATOS,
  destination: DESTINATIONS.LOGIN_SIGNUP,
  // Página de segmento vende AGENDA (o cliente marca hora): o teste que ela
  // oferece é o de Agendamento, e o `?produto=` leva isso ao signup.
  product: TRIAL_PRODUCTS.AGENDAMENTO,
};

/** Cadastro já carregando o produto — o backend escolhe o plano de trial. */
const SIGNUP_PATH = buildTrialSignupPath(TRIAL_PRODUCTS.AGENDAMENTO);

const LavaJatos = () => (
  <>
    <SeoHead
      title="Chatbot WhatsApp para Lava-Jato com Agenda e Upsell | Nexus Chatbot"
      description="Agenda lotada mesmo no fim de semana de chuva. Agendamentos, upsell automático e notificação de carro pronto pelo WhatsApp. Teste grátis 14 dias."
      canonical="https://nexustech.tec.br/lava-jatos"
      jsonLd={[SERVICE_LD, FAQ_LD]}
    />

    {/* Hero */}
    <section className={styles.hero}>
      <div className={styles.heroInner}>
        <div className={styles.heroCol}>
          <h1 className={styles.heroTitle}>
            <TbDroplet
              className={styles.titleIcon}
              size={38}
              aria-hidden="true"
            />
            Chatbot de WhatsApp para Lava-Jato — Agenda Cheia, Fila Zero e
            Upsell no Automático
          </h1>
          <p className={styles.heroText}>
            Distribua a demanda da sexta e sábado, ofereça serviços extras no
            momento do agendamento e avise o cliente quando o carro estiver
            pronto — sem nenhum esforço manual.
          </p>
          <Link
            to={SIGNUP_PATH}
            className={styles.ctaPrimary}
            onClick={() =>
              trackTrialCtaClick({
                ...CTA_CTX,
                cta_placement: CTA_PLACEMENTS.HERO,
              })
            }
          >
            Testar grátis 14 dias
          </Link>
          <p className={styles.heroNote}>
            Sem cartão. Cancele quando quiser. Configuração em 15 minutos.
          </p>
        </div>
        <div className={styles.heroMockup}>
          <WhatsAppMockup messages={LAVAJATOS_MOCKUP} looping />
        </div>
      </div>
    </section>

    {/* Dores */}
    <section className={styles.dores}>
      <div className={styles.doresInner}>
        <h2 className={styles.sectionTitle}>
          Por que lava-jatos usam a Nexus Tech
        </h2>
        <div className={styles.doresGrid}>
          {DORES.map((d, i) => (
            <div key={i} className={styles.doreCard}>
              <d.Icon
                className={styles.doreIcon}
                size={36}
                aria-hidden="true"
              />
              <h3 className={styles.doreTitle}>{d.title}</h3>
              <p className={styles.doreText}>{d.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA intermediário */}
    <section className={styles.ctaMid}>
      <div className={styles.ctaMidInner}>
        <p className={styles.ctaMidText}>
          Agenda cheia mesmo no fim de semana de chuva.
        </p>
        <Link
          to={SIGNUP_PATH}
          className={styles.ctaPrimary}
          onClick={() =>
            trackTrialCtaClick({
              ...CTA_CTX,
              cta_placement: CTA_PLACEMENTS.MIDDLE,
            })
          }
        >
          Testar grátis 14 dias
        </Link>
      </div>
    </section>

    {/* Recursos */}
    <section className={styles.recursos}>
      <div className={styles.recursosInner}>
        <h2 className={styles.sectionTitle}>
          Recursos pensados para lava-jatos
        </h2>
        <div className={styles.recursosGrid}>
          {RECURSOS.map((r, i) => (
            <div key={i} className={styles.recursoCard}>
              <TbCircleCheck
                className={styles.recursoIcon}
                size={20}
                aria-hidden="true"
              />
              <p className={styles.recursoText}>{r}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className={styles.faq}>
      <div className={styles.faqInner}>
        <h2 className={styles.faqTitle}>Perguntas frequentes</h2>
        <FaqAccordion items={LAVAJATOS_FAQ} />
      </div>
    </section>

    {/* CTA Final */}
    <section className={styles.ctaFinal}>
      <h2 className={styles.ctaFinalTitle}>
        Mais carros. Menos fila. Ticket maior.
      </h2>
      <p className={styles.ctaFinalText}>
        Agenda cheia mesmo no fim de semana de chuva.
      </p>
      <Link
        to={SIGNUP_PATH}
        className={styles.ctaInverse}
        onClick={() =>
          trackTrialCtaClick({
            ...CTA_CTX,
            cta_placement: CTA_PLACEMENTS.FINAL,
          })
        }
      >
        Testar grátis 14 dias
        <TbDroplet size={18} aria-hidden="true" />
        <TbSparkles size={18} aria-hidden="true" />
      </Link>
    </section>
  </>
);

export default LavaJatos;
